import { hasSignerSession } from './signer-session';

/**
 * Prefix Cognito uses for every persisted token key
 * (`CognitoIdentityServiceProvider.<userPoolClientId>.<…>` — idToken,
 * refreshToken, accessToken, LastAuthUser, …). These keys exist ONLY after a
 * successful Cognito sign-in and are cleared wholesale by `signOut()`, so their
 * presence is durable evidence that a real Cognito session was persisted.
 */
const COGNITO_KEY_PREFIX = 'CognitoIdentityServiceProvider.';

/**
 * True when the supplied Web Storage object currently holds at least one
 * Cognito-managed token key. Wrapped in try/catch because
 * `window.localStorage` / `window.sessionStorage` access can throw in privacy
 * modes / disabled storage; a thrown probe is treated as "no tokens here".
 */
function storageHasCognitoKeys(storage: Storage): boolean {
    try {
        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            if (key !== null && key.startsWith(COGNITO_KEY_PREFIX)) {
                return true;
            }
        }
        return false;
    } catch {
        return false;
    }
}

/**
 * True when there is durable evidence of a REAL persisted authentication
 * session that could plausibly be recovered — either Cognito tokens in
 * `localStorage`/`sessionStorage` (admins + SSO signers) or a valid self-issued
 * signer JWT (email-only signers, #2955).
 *
 * Used by {@link SessionRecovery} (issue #3194) to distinguish a genuinely
 * hung refresh of a real session (#2595 — tokens present → still recover) from
 * a never-authenticated cold-start settle (no tokens → stand down and let
 * `ProtectedRoute` own the `/login` redirect). Because a never-authenticated
 * cold start begins in Amplify's `'configuring'` state — not yet the settled
 * `'unauthenticated'` the earlier #2579 gate keyed on — the token probe is what
 * separates the two.
 */
export function hasPersistedSession(): boolean {
    return storageHasCognitoKeys(window.localStorage) || storageHasCognitoKeys(window.sessionStorage) || hasSignerSession();
}
